'use client'

/**
 * The client half of AI analysis: debounce the user's text, ask the route, hold the result.
 *
 * Out of `src/hooks/useEntryAnalysis.ts` on #397. Two things did not come with it. The tail was a
 * re-export block over the whole of `types/analysis.ts` — a hop, and hops do not survive a move
 * (#211) — so its readers import `./types` directly. And `MEDIA_TYPES`/`MediaType`/`isMediaType`
 * were a fourth spelling of `entries/`'s `MediaKind`; they are now `MEDIA_KINDS` and `isMediaKind`
 * there, which is the direction `capture/` may import.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { isMediaKind } from '@/entries'
import type {
  JournalAnalysis,
  TaskAnalysis,
  MovieAnalysis,
  BookAnalysis,
  ArtistAnalysis,
  LabelAnalysis,
  ReleaseAnalysis,
  AnalysisEntryType,
} from './types'

type EntryType = AnalysisEntryType | 'auto'

/** All possible analysis types */
export type AnyAnalysis =
  | JournalAnalysis
  | TaskAnalysis
  | MovieAnalysis
  | BookAnalysis
  | ArtistAnalysis
  | LabelAnalysis
  | ReleaseAnalysis

interface UseEntryAnalysisOptions {
  entryType: EntryType
  content: string
  availablePeople: { id: string; name: string }[]
  availableTopics?: string[]
  /** Full topic objects with entry type information for filtering */
  allTopicsWithTypes?: Array<{ name: string; entryType: string | null }>
  availableProjects?: string[]
  /** Minimum content length to trigger analysis (default: 20) */
  minContentLength?: number
  /** Debounce delay in ms (default: 700) */
  debounceMs?: number
  /** Whether analysis is enabled (default: true) */
  enabled?: boolean
}

interface UseEntryAnalysisResult<T> {
  analysis: T | null
  isAnalyzing: boolean
  error: string | null
  /** The detected entry type (only relevant when entryType is 'auto') */
  detectedType: AnalysisEntryType | null
  /** Whether the detected type is a media type (goes to Library) */
  isMedia: boolean
  /** Reset the analysis state */
  reset: () => void
}

export function useEntryAnalysis<T extends AnyAnalysis = AnyAnalysis>({
  entryType,
  content,
  availablePeople,
  availableTopics = [],
  allTopicsWithTypes = [],
  availableProjects = [],
  minContentLength = 20,
  debounceMs = 700,
  enabled = true,
}: UseEntryAnalysisOptions): UseEntryAnalysisResult<T> {
  const [analysis, setAnalysis] = useState<T | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detectedType, setDetectedType] = useState<AnalysisEntryType | null>(
    null,
  )

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastAnalyzedContentRef = useRef<string>('')

  // Store availablePeople and availableTopics in refs so they don't reset the debounce timer
  const availablePeopleRef = useRef(availablePeople)
  availablePeopleRef.current = availablePeople
  const availableTopicsRef = useRef(availableTopics)
  availableTopicsRef.current = availableTopics
  const allTopicsWithTypesRef = useRef(allTopicsWithTypes)
  allTopicsWithTypesRef.current = allTopicsWithTypes
  const availableProjectsRef = useRef(availableProjects)
  availableProjectsRef.current = availableProjects

  // Memoize people names for API call (only changes when actual names change)
  const peopleNamesKey = useMemo(
    () => availablePeople.map((p) => p.name).join(','),
    [availablePeople],
  )

  const reset = useCallback(() => {
    setAnalysis(null)
    setError(null)
    setIsAnalyzing(false)
    setDetectedType(null)
    lastAnalyzedContentRef.current = ''
  }, [])

  useEffect(() => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Skip if disabled or content too short
    if (!enabled || content.trim().length < minContentLength) {
      setIsAnalyzing(false)
      return
    }

    // Skip if content hasn't changed meaningfully
    if (content.trim() === lastAnalyzedContentRef.current.trim()) {
      return
    }

    // Set up debounced analysis
    debounceRef.current = setTimeout(async () => {
      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      setIsAnalyzing(true)
      setError(null)

      try {
        const response = await fetch('/api/analyze-entry', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            entryType,
            content: content.trim(),
            availablePeople: availablePeopleRef.current.map((p) => p.name),
            availableTopics: availableTopicsRef.current,
            topicsWithTypes: allTopicsWithTypesRef.current,
            availableProjects: availableProjectsRef.current,
          }),
          signal: controller.signal,
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || 'Analysis failed')
        }

        const data = await response.json()

        // Validate response structure
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid response format')
        }

        // Validate detectedType (now includes media types)
        const validTypes: readonly AnalysisEntryType[] = [
          'journal',
          'task',
          'movie',
          'book',
          'artist',
          'label',
          'release',
        ]
        const detectedTypeValue = data.detectedType as AnalysisEntryType
        if (!detectedTypeValue || !validTypes.includes(detectedTypeValue)) {
          throw new Error('Could not determine entry type')
        }

        // Validate analysis object exists
        if (!data.analysis || typeof data.analysis !== 'object') {
          throw new Error('No analysis data returned')
        }

        // Validate required fields based on entry type
        const analysisData = data.analysis as Record<string, unknown>

        if (detectedTypeValue === 'task') {
          if (typeof analysisData.category !== 'string') {
            throw new Error('Task analysis missing required field: category')
          }
          if (typeof analysisData.important !== 'boolean') {
            throw new Error('Task analysis missing required field: important')
          }
        } else if (isMediaKind(detectedTypeValue)) {
          // Media types need a title or name
          const hasTitle =
            typeof analysisData.title === 'string' ||
            typeof analysisData.name === 'string'
          if (!hasTitle) {
            throw new Error(`${detectedTypeValue} analysis missing title/name`)
          }
        }

        lastAnalyzedContentRef.current = content.trim()

        // Set both states together - React batches these updates
        setDetectedType(detectedTypeValue)
        setAnalysis(data.analysis as T)
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was aborted, ignore
          return
        }
        console.error('Entry analysis error:', err)
        setError(err instanceof Error ? err.message : 'Analysis failed')
        // Clear stale state on error
        setAnalysis(null)
        setDetectedType(null)
      } finally {
        setIsAnalyzing(false)
      }
    }, debounceMs)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
    // Only depend on content and entryType for debounce reset
    // peopleNamesKey ensures we use fresh people list when it actually changes
  }, [
    content,
    entryType,
    minContentLength,
    debounceMs,
    enabled,
    peopleNamesKey,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    analysis,
    isAnalyzing,
    error,
    detectedType,
    isMedia: isMediaKind(detectedType),
    reset,
  }
}
